import {
  SettingsApplicationSelectAll,
  SettingsAnswerEmptyValue,
  SettingsDependencies,
  TreeSelectOption,
  SettingsChangePriorityValue,
  SettingsQuestionNameVisibility,
  SettingsQuestionNameMandatory,
  QuestionCode,
  OnlyPIMSettings,
  OnlyOmniSettings,
  BaseSettings,
} from 'Constants/Settings'
import { getAnswers } from 'Services/Settings/SettingsService'
import {
  AnswerProp,
  QuestionGroup,
  RawAnswer,
  RespondingEntity,
  SettingsGroups,
  SettingsResponse,
  deleteAnswer,
  editAnswer,
  resetEdit,
  setSettingLoading,
} from 'Slices/Settings/SettingsSlice'
import { notification } from 'antd'
import { renderTranslation } from './GenericHelpers'
import { TranslationKeys } from 'Constants/TranslationKeys'

const setCollapse = (setterFunc: React.Dispatch<React.SetStateAction<any[]>>, settingName: string) => {
  setterFunc(oldArray => {
    if (oldArray.indexOf(settingName) === -1) {
      return [...oldArray, settingName]
    }

    return oldArray.filter(x => x !== settingName)
  })
}

const getFatherSettings = (settings: SettingsGroups, isOmnichannelActive: boolean, isImagesVisible: boolean) => {
  return Object.entries(settings)
    .filter(([key, value]: [string, QuestionGroup]) => {
      if (isImagesVisible && isOmnichannelActive) {
        return value && value.isVisible
      }
      if (isImagesVisible) {
        return value && value.isVisible && OnlyPIMSettings.includes(value.id)
      }
      if (isOmnichannelActive) {
        return value && value.isVisible && OnlyOmniSettings.includes(value.id)
      }
      return value && value.isVisible && BaseSettings.includes(value.id)
    })
    .sort((a, b) => a[1].priority - b[1].priority)
}

const getFatherSettingChilds = (questionGroup: QuestionGroup) => {
  return (questionGroup?.questionGroups ?? [])?.slice().sort((a, b) => a.priority - b.priority)
}

const getQuestionGroupChilds = (
  questionGroup: QuestionGroup,
  isOmnichannelActive: boolean,
  isImagesVisible: boolean,
) => {
  return (questionGroup?.questions ?? []).filter(question => {
    if (question.code === QuestionCode.OMNI_DECIMAL_ROUNDING) {
      return isOmnichannelActive && question.enabled
    }
    if (question.code === QuestionCode.SEASONS_NOT_TO_IMPORT_PIM) {
      return isImagesVisible && question.enabled
    }
    return question.enabled
  })
}

const getRawAnswerList = (settingName: string, answers: { [Key: string]: SettingsResponse }): RawAnswer[] => {
  const rawAnswers = answers[settingName]?.rawAnswers ?? []
  return rawAnswers as RawAnswer[]
}

const getRespondingEntityList = (
  settingName: string,
  answers: { [Key: string]: SettingsResponse },
): RespondingEntity[] => {
  const respondingEntity = answers[settingName]?.respondingEntities ?? []
  return respondingEntity as RespondingEntity[]
}

const getRawAnswerListFromEdit = (settingName: string, answers: { [Key: string]: RawAnswer[] }): RawAnswer[] => {
  const rawAnswers = answers[settingName] ?? []
  return rawAnswers
}

const getRawAnswer = (respondingEntityId: string, questionCode: string, answers: RawAnswer[]) => {
  const rawAnswer = answers?.find(
    cell => cell.questionCode === questionCode && cell.respondingEntityId === respondingEntityId,
  )

  return rawAnswer
}

const getSpecificRawAnswer = (
  respondingEntityId: string,
  questionCode: string,
  answers: RawAnswer[],
  answer: string,
) => {
  const rawAnswer = answers?.find(
    cell =>
      cell.questionCode === questionCode && cell.respondingEntityId === respondingEntityId && cell.answer === answer,
  )

  return rawAnswer
}

const getMultiAnswerRawAnswers = (
  respondingEntityId: string,
  questionCode: string,
  answers: RawAnswer[],
): AnswerProp[] => {
  const multiAnswers = answers
    ?.filter(cell => cell.questionCode === questionCode && cell.respondingEntityId === respondingEntityId)
    .map(x => ({ value: x.answer, priority: x.priority }))

  return multiAnswers
}

const generateRawAnswer = (
  value: string,
  questionCode: string,
  respondingEntityId: string,
  settingsName: string,
  priority: number = 0,
): RawAnswer => {
  return {
    answer: value,
    questionCode: questionCode,
    respondingEntityId: respondingEntityId,
    settingsName: settingsName,
    priority: priority,
  }
}

const mapRespondingEntity = (name: string, respondingEntity: RespondingEntity): RespondingEntity => {
  const entityCopy = { ...respondingEntity }
  entityCopy.name = name

  return entityCopy
}
const showError = (err, customMessage) => {
  notification.open({
    description: customMessage,
    placement: 'topRight',
    message: '',
    duration: 2.5,
  })
}

const generateRawAnswerFromEmptyValue = (
  respondingEntityId: string,
  questionCode: string,
  answers: RawAnswer[],
  settingsName: string,
): RawAnswer[] => {
  const rawAnswers = answers
    ?.filter(cell => cell.questionCode === questionCode && cell.respondingEntityId === respondingEntityId)
    .map(x => ({
      answer: x.answer,
      questionCode: questionCode,
      respondingEntityId: respondingEntityId,
      settingsName: settingsName,
      priority: x.priority,
    }))

  return rawAnswers
}

const getAlreadyRespondedValues = (question, editedAnswers) => {
  const questionCode = question.code
  let alreadyRespondedValues = question.alreadyRespondedValues.map(obj => {
    return { ...obj, code: questionCode }
  })

  if (editedAnswers) {
    alreadyRespondedValues = alreadyRespondedValues.filter(x => {
      return !editedAnswers.some(
        value => x.code === value.questionCode && x.respondingEntityId === value.respondingEntityId,
      )
    })
  }
  return alreadyRespondedValues
}

const filterDistinctValues = (options, valuesToCheck) => {
  if (!valuesToCheck) return options

  return options.map(item => {
    if (
      valuesToCheck.some(
        respondedItem =>
          item.value === respondedItem.answer &&
          item.value !== SettingsAnswerEmptyValue &&
          item.value !== SettingsApplicationSelectAll,
      )
    ) {
      return { ...item, priority: 1, checkable: false }
    } else {
      return item
    }
  })
}

const handleUpdateSettings = (dispatch, settingsGroup) => {
  const settingsToUpdate = new Set()
  for (const value of settingsGroup) {
    const settingsDeps = SettingsDependencies[value] || []
    settingsDeps.forEach(dependentSetting => {
      settingsToUpdate.add(dependentSetting)
    })
  }

  const promises = Array.from(settingsToUpdate).map(settingName => dispatch(getAnswers(settingName as string)))
  Promise.all(promises)
    .then(() => {
      dispatch(resetEdit())
      dispatch(setSettingLoading(false))
    })
    .catch(error => {
      console.error('Error updating settings:', error)
    })
}

const indexSettingsName = (items: any[]) => {
  return items.reduce((data, item) => {
    data[item.id] = item
    return data
  }, {} as { [id: string]: any })
}

//basic option creation
const createOption = (
  label: string,
  value: string,
  key: string,
  priority: number = 0,
  checkable: boolean = true,
  className: string = '',
): TreeSelectOption => {
  return {
    label,
    value,
    key,
    priority,
    checkable,
    className,
  }
}

//additional options: all, empty, change priority
const createAdditionalOptions = (
  language: string,
  enableChangePrio: boolean,
  enableEmpty: boolean,
  questionCode: string,
): TreeSelectOption[] => {
  const additionalOptions: TreeSelectOption[] = [
    createOption(
      renderTranslation(language, TranslationKeys.checkboxAllOnMultianswer),
      SettingsApplicationSelectAll,
      SettingsApplicationSelectAll,
    ),
    createOption(
      renderTranslation(language, TranslationKeys.checkboxEmptyOnMultianswer),
      SettingsAnswerEmptyValue,
      SettingsAnswerEmptyValue,
      0,
      true,
      enableEmpty ? 'empty-node' : 'empty-node disable',
    ),
  ]

  if (questionCode.includes('AutomaticComposition')) {
    additionalOptions.push(
      createOption(
        renderTranslation(language, TranslationKeys.changePriorityOption),
        SettingsChangePriorityValue,
        SettingsChangePriorityValue,
        0,
        enableChangePrio,
        enableChangePrio ? 'priority-node' : 'priority-node disable',
      ),
    )
  }

  return additionalOptions
}

const getPrioritySelectOptions = (value, virtualData) => {
  const availableValues = []
  virtualData.map(a => {
    availableValues.push({ value: a.priority, label: a.priority })
  })
  return availableValues
}

const emptyRespondingEntities = (question, editedAnswers, currentREId, dispatch) => {
  //When 'all' is triggered, empty every respEntitity Id except the one in which 'all' is clicked (only for onlyDistinctValues=true questions)
  const respondedValuesUniqueIds: string[] = Array.from(
    new Set(question.alreadyRespondedValues.map((item: { respondingEntityId: string }) => item.respondingEntityId)),
  )
  const editedAnswersUniqueIds: string[] =
    editedAnswers && Object.keys(editedAnswers).length > 0
      ? Array.from(
          new Set((editedAnswers as any[]).map((item: { respondingEntityId: string }) => item.respondingEntityId)),
        )
      : []
  const mergedUniqueIds: string[] = [...new Set([...respondedValuesUniqueIds, ...editedAnswersUniqueIds])]

  mergedUniqueIds.forEach(item => {
    if (item !== currentREId) {
      const emptyAnswer = []
      emptyAnswer.push(generateRawAnswer(SettingsAnswerEmptyValue, question.code, item, question.settingsName))
      dispatch(
        editAnswer({
          settingsName: question.settingsName,
          questionCode: question.code,
          respondingEntityId: item,
          isMultiAnswers: true,
          rawAnswer: generateRawAnswer(SettingsAnswerEmptyValue, question.code, item, question.settingsName),
        }),
      )
      dispatch(
        deleteAnswer({
          settingsName: question.settingsName,
          editInfo: {
            selectedAnswer: generateRawAnswer(SettingsAnswerEmptyValue, question.code, item, question.settingsName),
            editType: 'delete',
          },
        }),
      )
    }
  })
}

const getLastEditInfo = (data, values, question, respondingEntityId) => {
  //take the last added/removed value and generate the answer with its editType
  if (values.length < data.length) {
    const answerToDelete = data.filter(x => !values.find(item => item.label === x.label))
    return {
      selectedAnswer: generateRawAnswer(
        answerToDelete[0].value,
        question.code,
        respondingEntityId,
        question.settingsName,
        answerToDelete[0].priority,
      ),
      editType: 'delete',
    }
  } else {
    //if the added value is 'Empty' mark editType as delete, it will be handled on the reducer
    const answerToAdd = values.filter(x => !data.find(item => item.label === x.label))
    return {
      selectedAnswer: generateRawAnswer(
        answerToAdd[0].value,
        question.code,
        respondingEntityId,
        question.settingsName,
        values.length,
      ),
      editType: answerToAdd[0].value !== SettingsAnswerEmptyValue ? 'add' : 'delete',
    }
  }
}

const handleChangeCheck = (value, question, dispatch, storeValue, respondingEntityId) => {
  if (question.name === SettingsQuestionNameMandatory && value === 'false') {
    const visibilityQuestionCode = question.code.replace('IsMandatory', 'IsVisible')
    dispatch(
      editAnswer({
        settingsName: question.settingsName,
        questionCode: visibilityQuestionCode,
        respondingEntityId: respondingEntityId,
        rawAnswer: generateRawAnswer(value, visibilityQuestionCode, respondingEntityId, question.settingsName),
      }),
    )
  }
  storeValue(value)
}

const handleDisabledCheckbox = (question, userCanEdit, editedAnswers, respondingEntityId, answers) => {
  if (!userCanEdit) {
    return true
  }
  if (question.name === SettingsQuestionNameVisibility) {
    const mandatoryQuestionCode = question.code.replace('IsVisible', 'IsMandatory')
    const resultEditedAnswers = getRawAnswer(respondingEntityId, mandatoryQuestionCode, editedAnswers)
    const resultRawAnswers = getRawAnswer(respondingEntityId, mandatoryQuestionCode, answers?.rawAnswers)

    if (resultEditedAnswers) {
      return resultEditedAnswers.answer === 'false'
    }
    if (resultRawAnswers) {
      return resultRawAnswers.answer === 'false'
    }
  }

  return false
}

export {
  setCollapse,
  getRawAnswerList,
  getRawAnswer,
  generateRawAnswer,
  getFatherSettingChilds,
  getRawAnswerListFromEdit,
  getFatherSettings,
  mapRespondingEntity,
  getQuestionGroupChilds,
  getMultiAnswerRawAnswers,
  getRespondingEntityList,
  showError,
  generateRawAnswerFromEmptyValue,
  getAlreadyRespondedValues,
  filterDistinctValues,
  handleUpdateSettings,
  indexSettingsName,
  createOption,
  createAdditionalOptions,
  getPrioritySelectOptions,
  emptyRespondingEntities,
  getLastEditInfo,
  getSpecificRawAnswer,
  handleDisabledCheckbox,
  handleChangeCheck,
}
